import { useState } from 'react';
import { SECTORS, STATUSES, BUSINESS_SECTIONS, type Deal, type PeerRow, type ProvenancedField, type BusinessDetail } from '@krit/core';
import { startEnrich, enrichStatus, peersLookup } from '../lib/api.js';
import { Modal } from './Modal.js';
import { StagedLoader, InlineBusy } from './ui.js';

const ENRICH_STAGES = ['Searching the web', 'Reading sources', 'Extracting company profile', 'Resolving peers on screener.in', 'Done'];

const NUM = new Set(['rev26', 'rev25', 'pat26', 'pat25', 'ebitda26', 'valuationAsk', 'totalRaise', 'allocation', 'peNow', 'peIPO', 'de', 'ocfPat', 'wcDays', 'promoterStake', 'top3']);

const GROUPS: { title: string; fields: [keyof Deal, string][] }[] = [
  { title: 'Identity', fields: [['company', 'Company'], ['short', 'Short'], ['location', 'Location'], ['banker', 'Banker / MB'], ['poc', 'Krit POC'], ['dateCRM', 'Date in CRM']] },
  { title: 'Financials (₹ Cr)', fields: [['rev26', 'Revenue FY26'], ['rev25', 'Revenue FY25'], ['pat26', 'PAT FY26'], ['pat25', 'PAT FY25'], ['ebitda26', 'EBITDA FY26']] },
  { title: 'Deal terms & valuation (₹ Cr)', fields: [['valuationAsk', 'Valuation ask'], ['totalRaise', 'Total round'], ['allocation', 'Allocation'], ['peNow', 'Peer P/E now'], ['peIPO', 'Peer P/E @IPO'], ['ipo', 'IPO timeline']] },
  { title: 'Quality metrics', fields: [['de', 'Net D/E'], ['ocfPat', 'OCF/PAT %'], ['wcDays', 'WC days'], ['promoterStake', 'Promoter %'], ['top3', 'Top-3 %']] },
  { title: 'Profile', fields: [['founded', 'Founded'], ['employees', 'Employees'], ['facilities', 'Facilities'], ['promoter', 'Promoter'], ['ipoTarget', 'IPO target']] },
];
const inputCls = 'w-full px-2.5 py-2 bg-white border border-line rounded-lg text-[13px] text-navy outline-none transition-colors focus:border-teal focus:ring-2 focus:ring-teal/15';

export function EditDealModal({ deal, onClose, onSave, saving }: { deal: Deal; onClose: () => void; onSave: (patch: Partial<Deal>) => void; saving: boolean }) {
  const [f, setF] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    const dd = deal as unknown as Record<string, unknown>;
    for (const g of GROUPS) for (const [k] of g.fields) o[k as string] = String(dd[k as string] ?? '');
    o.sector = deal.sector; o.status = deal.status;
    o.business = deal.business; o.use = deal.use; o.tailwinds = deal.tailwinds; o.industry = deal.industry; o.notes = deal.notes;
    return o;
  });
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  // ── Listed peers (from the deck and/or web enrichment) ──────────────────
  const [peers, setPeers] = useState<string[]>(deal.peers ?? []);
  const [peerRows, setPeerRows] = useState<PeerRow[]>(deal.peerRows ?? []);

  // ── Enrichment agent (async: kick off → poll; provenanced; review before save) ──
  const [enr, setEnr] = useState<{ status: 'idle' | 'loading' | 'done' | 'error'; msg: string; sources: string[] }>({ status: 'idle', msg: '', sources: [] });
  const [prov, setProv] = useState<Record<string, ProvenancedField>>({});
  const [bd, setBd] = useState<BusinessDetail>(deal.businessDetail ?? {});
  async function enrich() {
    if (!f.company?.trim()) { setEnr({ status: 'error', msg: 'Add a company name first.', sources: [] }); return; }
    setEnr({ status: 'loading', msg: 'Starting enrichment agent…', sources: [] });
    setProv({});
    try {
      const { runId } = await startEnrich(deal.id, { company: f.company, sector: f.sector, location: f.location });
      let run: Awaited<ReturnType<typeof enrichStatus>> | null = null;
      for (let i = 0; i < 45; i++) { // ~45 × 3s ≈ 2¼ min max
        await new Promise((r) => setTimeout(r, 3000));
        run = await enrichStatus(deal.id, runId);
        if (run.status === 'running') { setEnr({ status: 'loading', msg: `${run.step ?? 'working…'} · ${run.creditsUsed} credits`, sources: [] }); continue; }
        break;
      }
      if (!run || run.status === 'running') { setEnr({ status: 'error', msg: 'Timed out — try again.', sources: [] }); return; }
      if (run.status === 'error' || !run.result) { setEnr({ status: 'error', msg: run.error ?? 'Enrichment failed.', sources: run.result?.sources ?? [] }); return; }
      const result = run.result;
      const detail = result.businessDetail ?? {};
      setBd(detail);
      setF((p) => {
        const next = { ...p };
        for (const [k, fld] of Object.entries(result.fields)) if (fld.value != null) next[k] = String(fld.value);
        // Keep the short-summary fields (consumed by Intake cards + IC memo) in sync.
        if (detail.summary?.content) next.business = detail.summary.content;
        if (detail.useOfProceeds?.content) next.use = detail.useOfProceeds.content;
        if (detail.tailwinds?.content) next.tailwinds = detail.tailwinds.content;
        return next;
      });
      setProv(result.fields);
      if (result.peers.length) {
        setPeers(result.peers);
        try { const { peers: funds } = await peersLookup(result.peers); if (funds.length) setPeerRows(funds.map((x) => ({ name: x.name, ticker: x.ticker, about: x.about ?? null, rev: x.revenue ?? 0, mcap: x.marketCap ?? 0, ipoYear: x.ipo?.ipoYear ?? '', brlm: x.ipo?.brlm ?? '', peList: x.ipo?.peList ?? '', pe: x.pe ?? 0, margin: x.netMargin ?? 0, issuePrice: x.ipo?.issuePrice ?? null, issueSize: x.ipo?.issueSize, subscription: x.ipo?.subscription, listingGain: x.ipo?.listingGain, ipoChecked: x.ipo?.checked }))); } catch { /* peer fundamentals are best-effort */ }
      }
      const n = Object.keys(result.fields).length;
      setEnr({ status: 'done', msg: `Agent filled ${n} field${n === 1 ? '' : 's'} · ${result.peers.length} peers · ${run.creditsUsed} credits · ${run.toolCalls} tool calls. Review & Save.`, sources: result.sources });
    } catch (e) {
      setEnr({ status: 'error', msg: String((e as Error).message), sources: [] });
    }
  }

  function save() {
    const patch: Record<string, unknown> = {};
    for (const g of GROUPS) for (const [k] of g.fields) patch[k as string] = NUM.has(k as string) ? Number(f[k as string]) : f[k as string];
    patch.sector = f.sector; patch.status = f.status;
    patch.business = f.business; patch.use = f.use; patch.tailwinds = f.tailwinds; patch.industry = f.industry; patch.notes = f.notes;
    patch.peers = peers; patch.peerRows = peerRows;
    if (Object.keys(bd).length) patch.businessDetail = bd;
    if (Object.keys(prov).length) patch.provenance = prov;
    onSave(patch as Partial<Deal>);
  }

  return (
    <Modal title={`Edit — ${deal.short}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line">Cancel</button>
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
      </>}>
      <div className="grid grid-cols-2 gap-2.5 mb-3">
        <div><label className="block text-[11px] font-semibold text-muted mb-1">Sector</label><select className={inputCls} value={f.sector} onChange={(e) => set('sector', e.target.value)}>{Object.keys(SECTORS).map((s) => <option key={s}>{s}</option>)}</select></div>
        <div><label className="block text-[11px] font-semibold text-muted mb-1">Status</label><select className={inputCls} value={f.status} onChange={(e) => set('status', e.target.value)}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
      </div>

      {/* Web enrichment */}
      <div className="flex flex-wrap items-center gap-2.5 mb-3 p-2.5 rounded-lg bg-canvas border border-line-2">
        <button
          type="button"
          onClick={enrich}
          disabled={enr.status === 'loading'}
          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-navy text-white disabled:opacity-60"
        >{enr.status === 'loading' ? <InlineBusy>Enriching…</InlineBusy> : '🌐 Enrich from web'}</button>
        {enr.status !== 'loading' && enr.status !== 'error' && (
          <span className={`text-[11.5px] flex-1 min-w-[180px] ${enr.status === 'done' ? 'text-green' : 'text-muted'}`}>
            {enr.msg || 'Pull peer P/E, promoter, founded, employees, facilities & rating from the web (Firecrawl).'}
          </span>
        )}
        {enr.status === 'loading' && <div className="w-full"><StagedLoader stages={ENRICH_STAGES} stageMs={8000} /></div>}
        {enr.status === 'error' && <div className="w-full"><StagedLoader stages={ENRICH_STAGES} error={enr.msg || 'Enrichment failed.'} onRetry={enrich} /></div>}
        {enr.sources.length > 0 && (
          <span className="text-[10.5px] text-muted-2 w-full">Sources: {enr.sources.map((s, i) => {
            const isUrl = /^https?:\/\//i.test(s);
            let label = s;
            if (isUrl) { try { label = new URL(s).hostname; } catch { /* keep raw */ } }
            return isUrl
              ? <a key={i} href={s} target="_blank" rel="noreferrer" className="underline mr-2 break-all">{label}</a>
              : <span key={i} className="mr-2 font-medium">{label}</span>;
          })}</span>
        )}
        {/* Provenance — where each enriched field came from (mandatory before an IC memo). */}
        {Object.keys(prov).length > 0 && (
          <div className="w-full mt-1 pt-2 border-t border-line-2 space-y-0.5">
            {Object.entries(prov).map(([k, fld]) => {
              const isUrl = fld.sourceUrl ? /^https?:\/\//i.test(fld.sourceUrl) : false;
              const tone = fld.confidence === 'high' ? 'text-green' : fld.confidence === 'medium' ? 'text-amber' : 'text-muted-2';
              return (
                <div key={k} className="text-[10.5px] text-muted flex flex-wrap gap-1.5">
                  <b className="text-navy capitalize">{k}</b>
                  <span className="text-navy">{String(fld.value)}</span>
                  <span className={tone}>· {fld.confidence}</span>
                  {fld.sourceUrl && (isUrl
                    ? <a href={fld.sourceUrl} target="_blank" rel="noreferrer" className="underline text-teal-d break-all">source</a>
                    : <span className="text-muted-2">· {fld.sourceUrl}</span>)}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Listed peers */}
      <div className="mb-3">
        <h4 className="text-navy text-[12.5px] font-semibold mb-1.5">Listed peers {peers.length > 0 && <span className="text-muted font-normal">· {peers.length}</span>}</h4>
        {peers.length === 0 ? (
          <p className="text-[11.5px] text-muted-2">None yet — use <span className="font-semibold">Enrich from web</span> above to pull comparable listed companies (they save to the deal's Peers tab).</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {peers.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-navy-50 text-navy text-[11.5px] font-medium">
                  {p}
                  <button type="button" onClick={() => setPeers(peers.filter((_, idx) => idx !== i))} className="text-muted hover:text-red leading-none">×</button>
                </span>
              ))}
            </div>
            {peerRows.length > 0 && (
              <div className="overflow-x-auto border border-line-2 rounded-lg">
                <table className="w-full text-[11.5px]">
                  <thead><tr className="text-muted text-[10px] uppercase">{['Peer', 'Revenue', 'Mkt cap', 'P/E', 'Margin'].map((h) => <th key={h} className="text-left font-bold px-2.5 py-1.5 border-b border-line-2">{h}</th>)}</tr></thead>
                  <tbody>
                    {peerRows.map((r, i) => (
                      <tr key={i} className="border-t border-line-2">
                        <td className="px-2.5 py-1.5 font-semibold text-navy">{r.name}</td>
                        <td className="px-2.5 py-1.5 mono">{r.rev ? `₹${r.rev} Cr` : '—'}</td>
                        <td className="px-2.5 py-1.5 mono">{r.mcap ? `₹${r.mcap} Cr` : '—'}</td>
                        <td className="px-2.5 py-1.5 mono">{r.pe ? `${r.pe}x` : '—'}</td>
                        <td className="px-2.5 py-1.5 mono">{r.margin ? `${r.margin}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Business detail (multi-section, from enrichment) — reviewed here, saved with the deal */}
      {Object.keys(bd).length > 0 && (
        <div className="mb-3">
          <h4 className="text-navy text-[12.5px] font-semibold mb-1.5">Business detail <span className="text-muted font-normal">· deck-first, saved with the deal</span></h4>
          <div className="space-y-1.5 border border-line-2 rounded-lg p-2.5 bg-canvas max-h-[220px] overflow-y-auto">
            {BUSINESS_SECTIONS.filter((s) => bd[s.key]?.content).map((s) => (
              <div key={s.key} className="text-[11.5px]">
                <b className="text-navy">{s.label}</b>{bd[s.key]?.sourceUrl && <span className="text-muted-2 text-[10px]"> · {bd[s.key]!.sourceUrl}</span>}
                <div className="text-muted leading-snug">{bd[s.key]!.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {GROUPS.map((g) => (
        <div key={g.title} className="mb-3">
          <h4 className="text-navy text-[12.5px] font-semibold mb-1.5">{g.title}</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {g.fields.map(([k, label]) => (
              <div key={k as string}>
                <label className="block text-[10.5px] font-semibold text-muted mb-1">{label}</label>
                <input className={inputCls} type={NUM.has(k as string) ? 'number' : 'text'} value={f[k as string]} onChange={(e) => set(k as string, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {(['business', 'use', 'tailwinds', 'industry', 'notes'] as const).map((k) => (
        <div key={k} className="mb-2.5">
          <label className="block text-[10.5px] font-semibold text-muted mb-1 capitalize">{k}</label>
          <textarea className={`${inputCls} min-h-[52px]`} value={f[k]} onChange={(e) => set(k, e.target.value)} />
        </div>
      ))}
    </Modal>
  );
}
